import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  travelCariFormSchema,
  normalizeTravelWebsite,
  parseTravelAmount,
  travelCariPaymentTerms,
  travelCariCurrencies,
  type TravelCariSource,
  type TravelCariFormValues,
} from '@polatkan/api/travel-cari';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { showSuccessToast } from '@/hooks/use-mutation-with-toast';
import {
  emptyTravelCariForm,
  changedTravelCariFields,
  accountErrorKey,
} from './account-form-model';

type FormInput = z.input<ReturnType<typeof travelCariFormSchema>>;
export interface TravelCariEditorAccount {
  id: string;
  source: TravelCariSource;
  updatedAt: Date;
  fields: TravelCariFormValues;
}
export interface SavedTravelCariAccount {
  id: string;
  source: TravelCariSource;
  firstName?: string;
  lastName?: string;
  email?: string;
}
interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: {
    code: string;
    source: TravelCariSource;
    label: string;
    organisation?: boolean;
    serviceKind?: string;
  };
  account?: TravelCariEditorAccount;
  onSaved?: (account: SavedTravelCariAccount) => void;
}

export function TravelCariAccountEditor({ open, onOpenChange, type, account, onSaved }: Props) {
  const { t } = useTranslation('travelCari');
  const { t: common } = useTranslation('common');
  const organisation = type.organisation ?? ['W', 'E'].includes(type.code);
  const commercial = type.source === 'partner' && type.serviceKind !== 'VENDOR';
  const schema = useMemo(
    () => travelCariFormSchema(type.source, organisation),
    [type.source, organisation]
  );
  const defaults = account?.fields ?? emptyTravelCariForm();
  const { register, handleSubmit, reset, setError, setFocus, setValue, getValues, formState } =
    useForm<FormInput, unknown, TravelCariFormValues>({
      resolver: zodResolver(schema),
      defaultValues: defaults,
      mode: 'onTouched',
      reValidateMode: 'onChange',
    });
  const { errors, isDirty, dirtyFields, isSubmitting, submitCount } = formState;
  const [saveError, setSaveError] = useState<string | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [hasConflict, setHasConflict] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [conflicts, setConflicts] = useState<
    Array<{ field: keyof TravelCariFormValues; latest: string; draft: string }>
  >([]);
  const session = useRef({ open: false, key: '', updatedAt: account?.updatedAt, fields: defaults });
  const create = trpc.travelCari.accounts.create.useMutation();
  const update = trpc.travelCari.accounts.update.useMutation();
  const utils = trpc.useUtils();
  const saving = isSubmitting || create.isPending || update.isPending || refreshing;
  const identity = account ? `${account.source}:${account.id}` : `${type.source}:${type.code}`;

  useEffect(() => {
    const shouldReset = open && (!session.current.open || session.current.key !== identity);
    session.current.open = open;
    session.current.key = identity;
    if (shouldReset) {
      session.current.updatedAt = account?.updatedAt;
      session.current.fields = account?.fields ?? emptyTravelCariForm();
      reset(account?.fields ?? emptyTravelCariForm());
      setSaveError(null);
      setDiscardOpen(false);
      setHasConflict(false);
      setConflicts([]);
      create.reset();
      update.reset();
    }
  }, [open, identity, account, reset, create.reset, update.reset]);

  const message = (value: string | undefined) =>
    t(`account.validation.${value ?? 'review'}`, { defaultValue: t('account.validation.review') });
  const fieldErrors = Object.entries(errors).filter(
    ([key, error]) => key !== 'root' && error?.message
  );
  const close = (next: boolean) => {
    if (saving) return;
    if (!next && isDirty) setDiscardOpen(true);
    else onOpenChange(next);
  };

  const submit = async (values: TravelCariFormValues) => {
    if (conflicts.length) return;
    setSaveError(null);
    let saved: SavedTravelCariAccount;
    try {
      saved = account
        ? await update.mutateAsync({
            id: account.id,
            source: account.source,
            expectedUpdatedAt: session.current.updatedAt!,
            ...changedTravelCariFields(values, dirtyFields),
          })
        : await create.mutateAsync({ ...values, source: type.source, cariTypeCode: type.code });
    } catch (error) {
      const key = accountErrorKey(error);
      setHasConflict(key === 'changed');
      if (key === 'emailExists' || key === 'nameExists') {
        setError(
          key === 'emailExists' ? 'email' : 'companyName',
          { type: 'server', message: key },
          { shouldFocus: true }
        );
      } else {
        const zodError = (
          error as { data?: { zodError?: { fieldErrors?: Record<string, string[]> } } }
        )?.data?.zodError;
        const issues = Object.entries(zodError?.fieldErrors ?? {}).filter(
          ([field]) => field in defaults
        );
        for (const [field, messages] of issues)
          setError(field as keyof FormInput, { type: 'server', message: messages[0] ?? 'review' });
        if (issues[0]) setFocus(issues[0][0] as keyof FormInput);
        setSaveError(t(`account.errors.${key}`));
      }
      return;
    }
    void utils.travelCari.invalidate();
    void utils.travelCariType.invalidate();
    void utils.customer.invalidate();
    void utils.partner.invalidate();
    showSuccessToast(t(account ? 'account.updated' : 'account.created'));
    onOpenChange(false);
    onSaved?.(saved);
  };

  const loadLatest = async () => {
    if (!account) return;
    setRefreshing(true);
    try {
      const values = getValues() as TravelCariFormValues;
      const draft = changedTravelCariFields(values, dirtyFields);
      const latest = await utils.travelCari.accounts.byId.fetch({
        source: account.source,
        id: account.id,
      });
      const overlapping = (Object.keys(draft) as Array<keyof TravelCariFormValues>).filter(
        (field) =>
          session.current.fields[field] !== latest.fields[field] &&
          draft[field] !== latest.fields[field]
      );
      reset(latest.fields);
      for (const field of Object.keys(draft) as Array<keyof TravelCariFormValues>)
        setValue(field, values[field], { shouldDirty: true, shouldValidate: true });
      session.current.updatedAt = latest.updatedAt;
      session.current.fields = latest.fields;
      setConflicts(
        overlapping.map((field) => ({
          field,
          latest: String(latest.fields[field]),
          draft: String(values[field]),
        }))
      );
      setHasConflict(false);
      setSaveError(null);
    } catch {
      setSaveError(t('account.loadFailed'));
    } finally {
      setRefreshing(false);
    }
  };
  const resolveConflict = (field: keyof TravelCariFormValues, keepDraft: boolean) => {
    if (!keepDraft)
      setValue(field, session.current.fields[field], { shouldDirty: true, shouldValidate: true });
    setConflicts((current) => current.filter((item) => item.field !== field));
  };

  const field = (
    name: keyof TravelCariFormValues,
    options: { required?: boolean; numeric?: boolean; type?: string; hint?: string } = {}
  ) => {
    const error = errors[name];
    return (
      <div className="space-y-1.5" key={name}>
        <Label htmlFor={`cari-${name}`}>
          {t(`account.fields.${name}`)}
          {options.required && (
            <span className="text-destructive ml-1" aria-hidden="true">
              *
            </span>
          )}
        </Label>
        <Input
          id={`cari-${name}`}
          type={options.type ?? 'text'}
          inputMode={options.numeric ? 'decimal' : undefined}
          autoComplete={name === 'email' ? 'email' : name === 'phone' ? 'tel' : undefined}
          aria-required={options.required}
          aria-invalid={!!error}
          aria-describedby={
            error ? `cari-${name}-error` : options.hint ? `cari-${name}-hint` : undefined
          }
          className={
            error
              ? 'border-destructive focus:border-destructive focus:ring-destructive/20'
              : undefined
          }
          {...register(
            name,
            options.numeric
              ? { setValueAs: parseTravelAmount }
              : name === 'website'
                ? {
                    setValueAs: normalizeTravelWebsite,
                    onBlur: (event) =>
                      setValue('website', normalizeTravelWebsite(event.target.value), {
                        shouldValidate: true,
                        shouldDirty: true,
                      }),
                  }
                : {}
          )}
        />
        {error ? (
          <p id={`cari-${name}-error`} className="text-destructive text-xs">
            {message(error.message)}
          </p>
        ) : (
          options.hint && (
            <p id={`cari-${name}-hint`} className="text-muted-foreground text-xs">
              {t(options.hint)}
            </p>
          )
        )}
      </div>
    );
  };
  const selectClass =
    'h-8 w-full border border-border bg-background px-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50';
  const currencies = [...new Set([...travelCariCurrencies, defaults.currency])];

  return (
    <>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent
          className="flex max-h-[calc(100dvh-2rem)] max-w-2xl flex-col gap-0 overflow-hidden p-0"
          closeDisabled={saving}
          onEscapeKeyDown={(event) => {
            if (saving) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (saving) event.preventDefault();
          }}
        >
          <DialogHeader className="border-b px-6 py-5 pr-12 text-left">
            <DialogTitle>{t(account ? 'account.edit' : 'account.new')}</DialogTitle>
            <DialogDescription>
              {type.code} · {type.label}
            </DialogDescription>
          </DialogHeader>
          <form
            noValidate
            onSubmit={handleSubmit(submit)}
            className="flex min-h-0 flex-col"
            aria-busy={saving}
          >
            <fieldset
              disabled={saving}
              className="min-h-0 min-w-0 space-y-6 overflow-y-auto px-6 py-5"
            >
              <p className="text-muted-foreground text-xs">{t('account.requiredHint')}</p>
              <section className="space-y-3" aria-labelledby="cari-identity-heading">
                <h3 id="cari-identity-heading" className="text-sm font-semibold">
                  {t('account.identity')}
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  {type.source === 'customer' && !organisation ? (
                    <>
                      {field('firstName', { required: true })}
                      {field('lastName')}
                    </>
                  ) : (
                    <>
                      {field('companyName', { required: true })}
                      {field('tradeName')}
                      {field('taxId')}
                      {field('registrationNo')}
                    </>
                  )}
                </div>
              </section>
              <section className="space-y-3" aria-labelledby="cari-contact-heading">
                <h3 id="cari-contact-heading" className="text-sm font-semibold">
                  {t('account.contact')}
                </h3>
                <p className="text-muted-foreground text-xs">{t('account.contactHint')}</p>
                {type.source === 'partner' && field('contactName')}
                {type.source === 'customer' && organisation && (
                  <div className="grid gap-4 sm:grid-cols-2">
                    {field('firstName')}
                    {field('lastName')}
                  </div>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  {field('email', { type: 'email' })}
                  {field('phone', { type: 'tel' })}
                  {type.source === 'customer'
                    ? field('secondaryPhone', { type: 'tel' })
                    : field('website', { hint: 'account.websiteHint' })}
                </div>
              </section>
              <section className="space-y-3" aria-labelledby="cari-address-heading">
                <h3 id="cari-address-heading" className="text-sm font-semibold">
                  {t('account.address')}
                </h3>
                {field('addressLine1')}
                <div className="grid gap-4 sm:grid-cols-2">
                  {field('city')}
                  {field('country')}
                </div>
              </section>
              <section className="space-y-3" aria-labelledby="cari-finance-heading">
                <h3 id="cari-finance-heading" className="text-sm font-semibold">
                  {t('account.finance')}
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="cari-currency">{t('account.fields.currency')}</Label>
                    <select id="cari-currency" className={selectClass} {...register('currency')}>
                      {currencies.map((currency) => (
                        <option key={currency}>{currency}</option>
                      ))}
                    </select>
                    {errors.currency && (
                      <p className="text-destructive text-xs">{message(errors.currency.message)}</p>
                    )}
                  </div>
                  {commercial && (
                    <div className="space-y-1.5">
                      <Label htmlFor="cari-paymentTerms">{t('account.fields.paymentTerms')}</Label>
                      <select
                        id="cari-paymentTerms"
                        className={selectClass}
                        {...register('paymentTerms')}
                      >
                        {travelCariPaymentTerms.map((term) => (
                          <option value={term} key={term}>
                            {t(`account.terms.${term}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {commercial &&
                    field('commissionRate', { numeric: true, hint: 'account.commissionHint' })}
                  {field('creditLimit', { numeric: true })}
                </div>
                {type.source === 'customer' && (
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" {...register('creditLimitEnabled')} />
                    {t('account.fields.creditLimitEnabled')}
                  </label>
                )}
              </section>
              <div className="space-y-1.5">
                <Label htmlFor="cari-notes">{t('account.fields.notes')}</Label>
                <Textarea
                  id="cari-notes"
                  rows={3}
                  {...register('notes')}
                  aria-invalid={!!errors.notes}
                />
                {errors.notes && (
                  <p className="text-destructive text-xs">{message(errors.notes.message)}</p>
                )}
              </div>
            </fieldset>
            <div className="bg-background shrink-0 space-y-3 border-t px-6 py-4">
              {submitCount > 0 && fieldErrors.length > 0 && (
                <div role="alert" className="text-destructive flex gap-2 text-sm">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="font-medium">{t('account.validation.review')}</p>
                    <ul className="mt-1 space-y-1">
                      {fieldErrors.map(([name, error]) => (
                        <li key={name}>
                          <button
                            type="button"
                            className="text-left underline underline-offset-2"
                            onClick={() => setFocus(name as keyof FormInput)}
                          >
                            {t(`account.fields.${name}`)}: {message(error?.message)}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {saveError && (
                <p role="alert" className="text-destructive text-sm">
                  {saveError}
                </p>
              )}
              {hasConflict && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={saving}
                  onClick={() => {
                    void loadLatest();
                  }}
                >
                  {t('account.loadLatest')}
                </Button>
              )}
              {conflicts.length > 0 && (
                <div className="max-h-48 space-y-3 overflow-auto text-sm" role="alert">
                  <p className="font-medium">{t('account.resolveConflicts')}</p>
                  {conflicts.map((item) => (
                    <div key={item.field} className="space-y-1 border p-3">
                      <p className="font-medium">{t(`account.fields.${item.field}`)}</p>
                      <p className="break-words text-xs">
                        {t('account.latestValue')}: {item.latest || t('account.notProvided')}
                      </p>
                      <p className="break-words text-xs">
                        {t('account.draftValue')}: {item.draft || t('account.notProvided')}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => resolveConflict(item.field, false)}
                        >
                          {t('account.useLatest')}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => resolveConflict(item.field, true)}
                        >
                          {t('account.keepDraft')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={saving}
                  onClick={() => close(false)}
                >
                  {common('cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={saving || conflicts.length > 0 || (!!account && !isDirty)}
                >
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t(saving ? 'account.saving' : account ? 'account.save' : 'account.create')}
                </Button>
              </DialogFooter>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={t('account.discardTitle')}
        description={t('account.discardDescription')}
        confirmLabel={t('account.discard')}
        cancelLabel={t('account.keepEditing')}
        onConfirm={() => {
          setDiscardOpen(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}
